import React, { useCallback, useEffect, useState } from 'react';
import { DollarSign, Wallet, Clock, AlertTriangle, RotateCcw, Landmark } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { useLiveRefresh } from '../../hooks/useLiveRefresh';

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Admin platform-wide finance snapshot — sourced entirely from the
 * centralized Financial Engine (/api/finance/summary, admin role).
 */
export default function PlatformFinanceCard() {
  const [summary, setSummary] = useState(null);

  const load = useCallback(() => {
    apiCall('/api/finance/summary')
      .then((d) => setSummary(d.summary))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useLiveRefresh(['finance', 'wallet', 'payroll', 'payments'], load);

  if (!summary) return null;

  const tiles = [
    { label: 'Payroll Volume', value: money(summary.payroll_volume_paid), Icon: DollarSign, tone: 'text-emerald-600 bg-emerald-50', testid: 'platform-finance-volume' },
    { label: 'Fees Collected', value: money(summary.platform_fees_collected), Icon: Landmark, tone: 'text-indigo-600 bg-indigo-50', testid: 'platform-finance-fees' },
    { label: 'Wallet Liability', value: money(summary.wallet_liability), Icon: Wallet, tone: 'text-blue-600 bg-blue-50', testid: 'platform-finance-liability' },
    { label: 'Pending Payroll', value: money(summary.payroll_pending_total), Icon: Clock, tone: 'text-amber-600 bg-amber-50', testid: 'platform-finance-pending' },
    { label: 'Payment Failures', value: summary.payment_failures ?? 0, Icon: AlertTriangle, tone: 'text-red-600 bg-red-50', testid: 'platform-finance-failures' },
    { label: 'Refunds', value: summary.refunds ?? 0, Icon: RotateCcw, tone: 'text-slate-600 bg-slate-100', testid: 'platform-finance-refunds' },
    { label: 'Chargebacks', value: summary.chargebacks ?? 0, Icon: AlertTriangle, tone: 'text-orange-600 bg-orange-50', testid: 'platform-finance-chargebacks' },
    { label: 'Marketplace Rev', value: money(summary.marketplace_commission_revenue), Icon: Landmark, tone: 'text-teal-600 bg-teal-50', testid: 'platform-finance-marketplace' },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="platform-finance-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">Platform Finance</h3>
        <span className="text-xs text-gray-400">{summary.ledger_events ?? 0} ledger events</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {tiles.map(({ label, value, Icon, tone, testid }) => (
          <div key={label} className="p-3 bg-gray-50 rounded-xl" data-testid={testid}>
            <div className={`inline-flex p-1.5 rounded-lg mb-1.5 ${tone}`}>
              <Icon className="w-4 h-4" />
            </div>
            <p className="text-sm font-semibold text-gray-900">{value}</p>
            <p className="text-xs text-gray-500 truncate" title={label}>{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
